import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { RM } from "@/components/shared";
import { Users, Wallet, ShieldAlert, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-hairline p-5 rounded-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.1em] text-slate">{label}</div>
      <div className="font-serif text-3xl md:text-4xl text-ink mt-2">{value}</div>
      {sub && <div className="text-xs text-slate mt-1">{sub}</div>}
    </div>
  );
}

const DQ_LABELS = {
  missing_email: "Tiada Emel / No Email",
  missing_mobile: "Tiada Mobil / No Mobile",
  missing_ic: "Tiada IC / No IC",
  missing_dob: "Tiada DOB / No DOB",
  unconfirmed_balance: "Baki Belum Sah / Unconfirmed Balance",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats)
    return (
      <Layout title="Papan Pemuka / Dashboard">
        <div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      </Layout>
    );

  return (
    <Layout title="Papan Pemuka / Dashboard" subtitle="Ringkasan keahlian & kualiti data / Membership & data quality overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Jumlah Ahli / Total Members" value={stats.total_members} sub={`${stats.active_members} active`} testid="kpi-total-members" />
        <Kpi label="Modal Syer / Total Shares (RM)" value={<RM value={stats.total_shares} />} sub="confirmed balances" testid="kpi-total-shares" />
        <Kpi label="Baki Belum Sah / Unconfirmed" value={stats.unconfirmed_balances} sub="need opening balance" testid="kpi-unconfirmed" />
        <Kpi label="Ahli Aktif / Active Members" value={stats.active_members} sub={Object.entries(stats.by_status).map(([k, v]) => `${v} ${k}`).join(" · ")} testid="kpi-status" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white border border-hairline rounded-sm">
          <div className="px-6 py-4 border-b border-hairline flex items-center gap-2">
            <ShieldAlert size={18} className="text-brass" />
            <h3 className="font-serif text-lg text-ink">Kualiti Data / Data Quality</h3>
          </div>
          <div className="divide-y divide-hairline">
            {Object.entries(stats.data_quality).map(([key, count]) => (
              <button
                key={key}
                onClick={() => nav(`/members?missing=${key}`)}
                data-testid={`dq-${key}`}
                className="w-full flex items-center justify-between px-6 py-3.5 hover:bg-paper transition-colors text-left group"
              >
                <span className="text-sm text-slate">{DQ_LABELS[key] || key}</span>
                <span className="flex items-center gap-3">
                  <span className={`font-mono text-sm font-semibold ${count > 0 ? "text-brick" : "text-positive"}`}>{count}</span>
                  <ArrowRight size={14} className="text-slate/40 group-hover:text-brass transition-colors" />
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-hairline rounded-sm p-6 flex flex-col">
          <h3 className="font-serif text-lg text-ink mb-4">Tindakan Pantas / Quick Actions</h3>
          <button onClick={() => nav("/members")} className="w-full flex items-center gap-3 px-4 py-3 border border-hairline rounded-sm hover:bg-paper transition-colors text-sm text-ink mb-3" data-testid="qa-members">
            <Users size={18} className="text-brass" /> Lihat Daftar Ahli / View Register
          </button>
          <button onClick={() => nav("/import")} className="w-full flex items-center gap-3 px-4 py-3 border border-hairline rounded-sm hover:bg-paper transition-colors text-sm text-ink mb-3" data-testid="qa-import">
            <Wallet size={18} className="text-brass" /> Import Excel Register
          </button>
          <button onClick={() => nav("/reconciliation")} className="w-full flex items-center gap-3 px-4 py-3 border border-hairline rounded-sm hover:bg-paper transition-colors text-sm text-ink" data-testid="qa-recon">
            <CheckCircle2 size={18} className="text-brass" /> Run Reconciliation
          </button>
        </div>
      </div>
    </Layout>
  );
}
